import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { DateTime } from 'luxon';
import { forkJoin, from, Observable, of, throwError } from 'rxjs';
import { catchError, map, mergeMap, take, tap, withLatestFrom } from 'rxjs/operators';

import { env } from '@amfam/environment';
import { Policy } from '@amfam/policy/models/lib/models/policy';
import { SourceSystem } from '@amfam/policy/models/lib/models/source-system-type';
import { AddressPipe, PrettyPolicyNum } from '@amfam/policy/pipes/lib/pipes/policy.pipes';
import { AnalyticsFacade } from '@amfam/shared/analytics/lib/+state/analytics.facade';
import { DataDogService } from '@amfam/shared/analytics/lib/services/data-dog.service';
import { AgentInfo } from '@amfam/shared/models/lib/agent/agent';
import { PolicyChangeRequestType } from '@amfam/shared/models/lib/policy/policy-change.model';
import { TypeOfPolicy } from '@amfam/shared/models/lib/policy/policy-view';
import {
  ArrayToList,
  PhoneNumberSingleStringFormatterPipe,
  PrettyPhoneNumberPipe,
  TitleCasePipe
} from '@amfam/shared/ui/pipes/lib/pipes/format.pipe';
import {
  selectAddress,
  selectCustomerId,
  selectDisplayName,
  selectEmailAddress,
  selectFirstName,
  selectLastName,
  selectPartyDetails
} from '@amfam/shared/user/lib/+state/user.selectors';
import { Agent } from '@amfam/shared/utility/agent/lib/models/agent.model';
import { fromRouterActions } from '@amfam/shared/utility/navigation/lib/+state/router-actions';
import { CommunicationService } from '@amfam/shared/utility/shared-services/lib/communication/communication.service';
import {
  Addressee,
  InternalEmail
} from '@amfam/shared/utility/shared-services/lib/communication/models/internalemail.model';
import { MessageDetail } from '@amfam/shared/utility/shared-services/lib/communication/models/messagedetail.model';
import { TemplatedEmail } from '@amfam/shared/utility/shared-services/lib/communication/models/templatedemail.model';
import { CopyService } from '@amfam/shared/utility/shared-services/lib/copy/copy.service';
import { UtilService } from '@amfam/shared/utility/shared-services/lib/core/util.service';
import { omit } from '@amfam/shared/utility/shared-services/lib/utils/object.utils';
import { interpolate } from '@amfam/shared/utility/shared-services/lib/utils/string.utils';

import {
  selectAgentInformationByProducerId,
  selectSelectedAgentFromStoreFactory
} from '../../core/store';
import { ANSRequest } from '../models/change-request-ANS.model';
import { ChangerequestInternalEmail } from '../models/change-request-internal-email.model';
import { PolicyAnalyticActionPayload } from '../models/change-request-policies.model';
import { ChangeRequest, RequestChange } from '../models/change-request.model';
import { PolicyTransmuteService } from './policy-transmute.service';

type AgentEmailOutcome =
  | { success: true; agentId: string }
  | { success: false; agentId: string; error: unknown };

@Injectable({
  providedIn: 'root'
})
export class PlchangerequestService {
  private communicationService = inject(CommunicationService);
  private store = inject(Store);
  private http = inject(HttpClient);
  private utilService = inject(UtilService);
  private analyticsFacade = inject(AnalyticsFacade);
  private policyTransmuteService = inject(PolicyTransmuteService);
  private copyService = inject(CopyService);
  private dataDogService = inject(DataDogService);

  private getANSPolicyNumbers(model: ChangeRequest) {
    let policyNumbers = model.requestChange?.afiPolicyNumbers;
    policyNumbers = policyNumbers || [];
    return policyNumbers;
  }

  private transformCoveragePayload(isAfi: boolean, model: ChangeRequest) {
    const afiPayload = {
      ...model,
      requestChange: {
        ...model.requestChange,
        policyNumbers: model.requestChange?.afiPolicyNumbers?.join(',') ?? '',
        isPartner: false
      },
      coverageChange: {
        ...model.coverageChange,
        policyList: model.coverageChange?.policyList.filter(
          p => model.requestChange?.afiPolicyNumbers?.includes(p.policyNumber) ?? false
        )
      }
    };

    const partnerPayload = {
      ...model,
      requestChange: {
        ...model.requestChange,
        policyNumbers: model.requestChange?.partnerPolicyNumbers?.join(',') ?? '',
        isPartner: false
      },
      coverageChange: {
        ...model.coverageChange,
        policyList: model.coverageChange?.policyList.filter(
          p => model.requestChange?.partnerPolicyNumbers?.includes(p.policyNumber) ?? false
        )
      }
    };

    return isAfi ? afiPayload : partnerPayload;
  }

  private buildEmailContent(model: ChangeRequest) {
    let emailContent = '';
    const requestedChange = this.policyTransmuteService.getChangeRequestTypeText(
      model.policyChangeType ?? null
    );
    switch (model.policyChangeType) {
      case PolicyChangeRequestType.NAME_CHANGE:
        emailContent = this.copyService.getCopy(
          `policy.${model.requestChange?.isPartner ? 'policyNameChangeAdvisorEmail' : 'policyNameChangeAgentEmail'}`,
          {
            agentName: model.requestChange?.agentName ?? '',
            existingName: model.nameChange?.existingName,
            newName: model.nameChange?.newName,
            mailingAddress: model.nameChange?.mailingAddress,
            emailAddress: model.nameChange?.emailAddress,
            phoneNumber: model.nameChange?.phoneNumber,
            notes: model.nameChange?.notes,
            policyNumbers: model.requestChange?.isPartner
              ? model.requestChange?.policyNumbers
              : model.requestChange?.afiPolicyNumbers
          }
        );
        break;

      case PolicyChangeRequestType.ADDRESS_CHANGE:
        emailContent = this.copyService.getCopy(
          `policy.${model.addressChange?.vehiclesGaraged === 'Yes' ? 'vehicleAddressChangeEmail' : 'addressChangeLicensedAdvisorEmail'}`,
          {
            agentName: model.addressChange?.agentName,
            customerName: model.addressChange?.customerName,
            policyNumbers: model.requestChange?.isPartner
              ? model.requestChange.partnerPolicyNumbers
              : model.requestChange?.afiPolicyNumbers,
            existingAddress: model.addressChange?.existingAddress,
            newAddress: model.addressChange?.newAddress,
            emailAddress: model.addressChange?.emailAddress,
            phoneNumbers: model.addressChange?.phoneNumbers,
            requestedChange,
            sendDocuments: model.addressChange?.sendDocuments,
            vehiclesGaraged: model.addressChange?.vehiclesGaraged,
            vehicles: model.addressChange?.vehicles,
            reason: model.addressChange?.reason
          }
        );
        break;
      case PolicyChangeRequestType.DRIVER_LICENSE_CHANGE:
        try {
          emailContent = this.copyService.getCopy('policy.driverLicenseChange.advisorEmail.body', {
            requestedChange,
            customerId: model.requestChange?.customerId,
            customerName: model.requestChange?.customerName,
            emailAddress: model.requestChange?.emailAddress,
            mailingAddress: model.requestChange?.mailingAddress,
            phoneNumbers: model.requestChange?.phoneNumbers,
            licenseNumber: model.driverLicenseChange?.licenseNumber,
            state: model.driverLicenseChange?.state,
            driver: model.driverLicenseChange?.driver,
            reason: model.driverLicenseChange?.reason
          });
        } catch (e) {
          this.dataDogService.log('Error in driver license change email template', 'error', {
            error: e,
            changeRequestType: PolicyChangeRequestType.DRIVER_LICENSE_CHANGE
          });
          if (env.environment !== 'prod') {
            console.log(e);
          }
        }
        break;

      case PolicyChangeRequestType.VIN_CHANGE:
        emailContent = this.copyService.getCopy(
          `policy.vinChange.${model.vinChange?.isAfi ? 'agentEmail' : 'advisorEmail'}.body`,
          {
            agentName: model.requestChange?.agentName,
            customerName: model.requestChange?.customerName,
            policyNumbers: model.vinChange?.policyNumber,
            mailingAddress: model.requestChange?.mailingAddress,
            phoneNumbers: model.requestChange?.phoneNumbers,
            emailAddress: model.requestChange?.emailAddress,
            requestedChange,
            whyUpdateVin: model.vinChange?.whyUpdateVin,
            affectedVehicle: model.vinChange?.vehicle,
            currentVin: model.vinChange?.currentVin,
            currentYear: model.vinChange?.currentYear,
            newVin: model.vinChange?.newVin,
            newYear: model.vinChange?.newYear,
            make: model.vinChange?.make || 'N/A',
            model: model.vinChange?.model || 'N/A',
            series: model.vinChange?.series || 'N/A',
            reason: model.vinChange?.reason
          }
        );
        break;

      case PolicyChangeRequestType.MORTGAGE_LENDER_DETAILS_CHANGE: {
        let lenderDetails = `<br><b>Requested change:</b> ${requestedChange}`;
        let contactDetails = '';
        const requestChange = model.requestChange;
        const mortgageInfo = model.mortgageChange;
        if (requestChange?.phoneNumbers) {
          contactDetails += `<br><b>Customer phone:</b> ${requestChange.phoneNumbers}`;
        }
        if (requestChange?.emailAddress) {
          contactDetails += `<br><b>Customer email address:</b> ${requestChange.emailAddress}`;
        }
        if (mortgageInfo?.currentLenderName)
          lenderDetails += `<br><b>Current lender name:</b> ${mortgageInfo?.currentLenderName}`;
        if (mortgageInfo?.newLenderName || mortgageInfo?.addLenderName)
          lenderDetails += `<br><b>New lender name:</b> ${
            mortgageInfo?.newLenderName || mortgageInfo?.addLenderName
          }`;
        if (mortgageInfo?.lenderToBeRemoved)
          lenderDetails += `<br><b>Lender to be removed:</b> ${mortgageInfo?.lenderToBeRemoved}`;
        if (mortgageInfo?.comment) lenderDetails += `<br><b>Comment:</b> ${mortgageInfo?.comment}`;

        emailContent = this.copyService.getCopy(
          `policy.${mortgageInfo?.isAfi ? 'policyMortgageLenderChangeAgentEmail' : 'policyMortgageLenderChangeAdvisorEmail'}`,
          {
            agentName: mortgageInfo?.isAfi ? requestChange?.agentName : null,
            existingName: requestChange?.customerName,
            policyNumber: mortgageInfo?.selectedPolicyNumber,
            contactDetails,
            lenderDetails
          }
        );
        break;
      }

      case PolicyChangeRequestType.POLICY_COVERAGE_CHANGE: {
        const afiPayload = this.transformCoveragePayload(true, model);
        const partnerPayload = this.transformCoveragePayload(false, model);

        const payload = model.requestChange?.isPartner ? partnerPayload : afiPayload;
        const requestChange = payload.requestChange;
        const coverageInfo = payload.coverageChange;
        emailContent = this.copyService.getCopy(
          requestChange.isPartner
            ? 'policy.policyCoverageChangeAdvisorEmail'
            : 'policy.policyCoverageChangeAgentEmail',
          {
            agentName: requestChange.agentName,
            existingName: requestChange.customerName,
            mailingAddress: requestChange.mailingAddress,
            emailAddress: requestChange.emailAddress,
            phoneNumber: requestChange.phoneNumbers,
            policytext: (coverageInfo?.policyList?.length ?? 0) > 0 ? 'Policies' : 'Policy',
            policiesWithRiskDescription: new ArrayToList().transform(
              (coverageInfo?.policyList ?? []).flatMap((policy: Policy) => {
                const riskDescription = `<br> Risk Description: ${new ArrayToList().transform(
                  policy.riskDescriptionList ?? []
                )} <br>
                    Policy Number: ${new PrettyPolicyNum().transform(policy)}`;
                return riskDescription;
              }),
              '<br>'
            ),
            notes: coverageInfo?.comment
          }
        );
        break;
      }

      default:
        break;
    }
    return emailContent;
  }

  private buildANSContent(model: ChangeRequest) {
    let ansContent = '';
    const requestedChange = this.policyTransmuteService.getChangeRequestTypeText(
      model.policyChangeType
    );
    switch (model.policyChangeType) {
      case PolicyChangeRequestType.NAME_CHANGE:
        ansContent = this.copyService.getCopy(`policy.policyNameChangeANSMessage`, {
          agentName: model.requestChange?.agentName,
          existingName: model.nameChange?.existingName,
          newName: model.nameChange?.newName,
          mailingAddress: model.nameChange?.mailingAddress,
          emailAddress: model.nameChange?.emailAddress,
          phoneNumber: model.nameChange?.phoneNumber,
          notes: model.nameChange?.notes,
          policyNumbers: model.requestChange?.afiPolicyNumbers
        });
        break;

      case PolicyChangeRequestType.ADDRESS_CHANGE:
        ansContent = this.copyService.getCopy('policy.addressChangeANSMessage', {
          agentName: model.addressChange?.agentName,
          customerName: model.addressChange?.customerName,
          policyNumbers: model.requestChange?.afiPolicyNumbers,
          existingAddress: model.addressChange?.existingAddress,
          newAddress: model.addressChange?.newAddress,
          emailAddress: model.addressChange?.emailAddress,
          phoneNumbers: model.addressChange?.phoneNumbers,
          requestedChange,
          sendDocuments: model.addressChange?.sendDocuments,
          vehiclesGaraged: model.addressChange?.vehiclesGaraged,
          vehicles: model.addressChange?.vehicles,
          reason: model.addressChange?.reason
        });
        break;
      case PolicyChangeRequestType.DRIVER_LICENSE_CHANGE:
        ansContent = this.copyService.getCopy('policy.driverLicenseChangeANSMessage', {
          agentName: model.driverLicenseChange?.agentName,
          customerName: model.driverLicenseChange?.customerName,
          policyNumbers: model.requestChange?.afiPolicyNumbers,
          existingAddress: model.driverLicenseChange?.mailingAddress,
          phoneNumbers: model.driverLicenseChange?.phoneNumbers,
          emailAddress: model.driverLicenseChange?.emailAddress,
          requestedChange,
          driver: model.driverLicenseChange?.driver,
          licenseNumber: model.driverLicenseChange?.licenseNumber,
          state: model.driverLicenseChange?.state,
          reason: model.driverLicenseChange?.reason
        });
        break;
      case PolicyChangeRequestType.VIN_CHANGE:
        ansContent = this.copyService.getCopy('policy.vinChange.agentANS', {
          agentName: model.requestChange?.agentName,
          customerName: model.requestChange?.customerName,
          policyNumbers: model.vinChange?.policyNumber,
          mailingAddress: model.requestChange?.mailingAddress,
          phoneNumbers: model.requestChange?.phoneNumbers,
          emailAddress: model.requestChange?.emailAddress,
          requestedChange,
          whyUpdateVin: model.vinChange?.whyUpdateVin,
          affectedVehicle: model.vinChange?.vehicle,
          currentVin: model.vinChange?.currentVin,
          currentYear: model.vinChange?.currentYear,
          newVin: model.vinChange?.newVin,
          newYear: model.vinChange?.newYear,
          make: model.vinChange?.make || 'N/A',
          model: model.vinChange?.model || 'N/A',
          series: model.vinChange?.series || 'N/A',
          reason: model.vinChange?.reason
        });
        break;

      case PolicyChangeRequestType.MORTGAGE_LENDER_DETAILS_CHANGE: {
        let lenderDetails = `\nRequested change: ${requestedChange}`;
        let contactDetails = '';
        const requestChange = model.requestChange;
        if (requestChange?.phoneNumbers) {
          contactDetails += `\nCustomer phone: ${requestChange.phoneNumbers}`;
        }
        if (requestChange?.emailAddress) {
          contactDetails += `\nCustomer email address: ${requestChange.emailAddress}`;
        }
        if (model.mortgageChange?.currentLenderName)
          lenderDetails += `\nCurrent lender name: ${model.mortgageChange?.currentLenderName}`;
        if (model.mortgageChange?.newLenderName || model.mortgageChange?.addLenderName)
          lenderDetails += `\nNew lender name: ${
            model.mortgageChange?.newLenderName || model.mortgageChange?.addLenderName
          }`;
        if (model.mortgageChange?.lenderToBeRemoved)
          lenderDetails += `\nLender to be removed: ${model.mortgageChange?.lenderToBeRemoved}`;
        if (model.mortgageChange?.comment)
          lenderDetails += `\nComment: ${model.mortgageChange?.comment}`;

        ansContent = this.copyService.getCopy('policy.policyMortgageLenderChangeANSMessage', {
          agentName: requestChange?.agentName,
          existingName: requestChange?.customerName,
          policyNumber: model.mortgageChange?.selectedPolicyNumber,
          contactDetails,
          lenderDetails
        });
        break;
      }

      case PolicyChangeRequestType.POLICY_COVERAGE_CHANGE: {
        const afiPayload = this.transformCoveragePayload(true, model);
        const partnerPayload = this.transformCoveragePayload(false, model);

        this.dataDogService.log('Policy coverage change payloads', 'info', {
          afiPayloadPolicies: afiPayload.requestChange?.policyNumbers,
          partnerPayloadPolicies: partnerPayload.requestChange?.policyNumbers,
          isPartner: model.requestChange?.isPartner
        });
        if (env.environment !== 'prod') {
          console.log('AFI ANS', afiPayload);
          console.log('Partner ANS', partnerPayload);
        }

        const payload = model.requestChange?.isPartner ? partnerPayload : afiPayload;
        const requestChange = payload.requestChange;
        const coverageInfo = payload.coverageChange;
        ansContent = this.copyService.getCopy('policy.policyCoverageChangeANSMessage', {
          agentName: requestChange.agentName,
          existingName: requestChange.customerName,
          mailingAddress: requestChange.mailingAddress,
          emailAddress: requestChange.emailAddress,
          phoneNumber: requestChange.phoneNumbers,
          policytext: (coverageInfo?.policyList?.length ?? 0) > 0 ? 'Policies' : 'Policy',
          policiesWithRiskDescription: new ArrayToList().transform(
            (coverageInfo?.policyList ?? []).flatMap(
              (policy: Policy) =>
                `${policy.policyNumber} - ${new ArrayToList().transform(
                  policy.riskDescriptionList ?? []
                )}`
            )
          ),
          notes: coverageInfo?.comment
        });
        break;
      }

      default:
        break;
    }
    return ansContent;
  }

  public sendAgentEmail(
    model: ChangeRequest,
    sendFn: (request: ChangeRequest) => Observable<unknown> = request => this.sendEmail(request)
  ): Observable<unknown> {
    const agents = model?.requestChange?.agents;

    if (agents && agents.length > 0) {
      const calls: Observable<AgentEmailOutcome>[] = agents.map(agent =>
        sendFn(this.applyAgentToModel(model, agent)).pipe(
          map((): AgentEmailOutcome => ({ success: true, agentId: agent.agentId })),
          catchError(
            (error): Observable<AgentEmailOutcome> =>
              of({ success: false, agentId: agent.agentId, error })
          )
        )
      );
      return forkJoin(calls).pipe(
        mergeMap(results => {
          if (results.some(r => r.success)) return of(results);
          const firstFailure = results.find(
            (r): r is Extract<AgentEmailOutcome, { success: false }> => !r.success
          );
          return throwError(() => firstFailure?.error);
        })
      );
    }

    return sendFn(model);
  }

  private applyAgentToModel(model: ChangeRequest, agent: AgentInfo): ChangeRequest {
    const baseRequestChange = model.requestChange;
    if (!baseRequestChange) return model;
    const requestChange: RequestChange = {
      ...baseRequestChange,
      agentId: agent.agentId,
      agentName: agent.name,
      agencyEmail: agent.agencyEmails?.at(0) ?? '',
      agentEmail: agent.emails?.at(0) ?? ''
    };
    return { ...model, requestChange };
  }

  sendEmail(model: ChangeRequest): Observable<unknown> {
    try {
      const emailContent = this.buildEmailContent(model);
      const agentEmail = model.requestChange?.agencyEmail || model.requestChange?.agentEmail;
      const internalAgentEmail = model.requestChange?.agencyEmail
        ? env.policyGracePeriodNotificatonInternalAgencyMailbox
        : env.policyGracePeriodNotificatonInternal;
      const emailAddress = env.production ? agentEmail : internalAgentEmail;
      if (!emailAddress) {
        return throwError(() => new Error('Agent Email not found'));
      }
      let email: InternalEmail = new InternalEmail();
      email = {
        ...email,
        messageFrom: new Addressee(env.policyChangeRequestInternal, 'Policy Change Request'),
        messageToList: [new Addressee(emailAddress ?? '', model.requestChange?.agentName)],
        messageSubject: this.copyService.getCopy('policy.addressChange.agentEmail.subject'),
        messageContent: emailContent
      };
      return this.communicationService.sendInternalEmail(email);
    } catch {
      return of(null);
    }
  }

  public sendAdvisorEmail(model: ChangeRequest) {
    try {
      const emailContent = this.buildEmailContent(model);
      const emailAddress: string = env.partnerLicensedAdvisorEmail;
      // If we do not have the agent email throw an error
      if (!emailAddress) {
        throwError(
          () =>
            new Error(this.copyService.getCopy('policy.requestChangePartner.advisorEmailNotFound'))
        );
      }
      let email: InternalEmail = new InternalEmail();
      email = {
        ...email,
        messageFrom: new Addressee(
          'policy_change_request@amfam.com',
          this.copyService.getCopy('policy.requestChangePartner.policyChangeRequest')
        ),
        messageToList: [new Addressee(emailAddress, 'Advisor')],
        messageSubject: this.copyService.getCopy('policy.driverLicenseChange.advisorEmail.subject'),
        messageContent: emailContent
      };
      return this.communicationService.sendInternalEmail(email);
    } catch {}
  }

  public sendANS(model: ChangeRequest) {
    const policyNumbers = this.getANSPolicyNumbers(model);
    try {
      const ansContent = this.buildANSContent(model);
      const ansRequest: ANSRequest = {
        notificationsRequest: {
          notificationText: ansContent,
          notificationType: 'ANSMYREQCH',
          customerName: model.requestChange?.customerName ?? '',
          referenceNumber: policyNumbers.join(',') || 'NA',
          formattedReferenceNumber: policyNumbers.join(',') || 'NA',
          policyNumber: policyNumbers.join(','),
          from: 'DMSI',
          dueDate: DateTime.now().plus({ days: 90 }).toJSDate().toISOString(),
          createdBy: 'DMSI',
          uniqueId: this.utilService.generateId(),
          sourceSystem: 'MYACCOUNT'
        },
        producerId: model.requestChange?.agentId ?? ''
      };
      return this.ansNotification(ansRequest);
    } catch (error) {
      this.dataDogService.log('Error in ANS notification request', 'error', {
        error: error,
        changeRequestType: model.policyChangeType?.toString
      });
      if (env.environment !== 'prod') {
        console.log('EEEEEEEE', error);
      }
      return of(null);
    }
  }

  public sendCustomerEmail(model: ChangeRequest) {
    const agents = model?.requestChange?.agents;
    const agentIds = agents?.length ? agents.map(a => a.agentId).join(',') : undefined;
    let partnerId: string | null = null;
    let templateCode: 'MYAC_TPI' | 'MRTC' = 'MRTC';
    const requestedChange = this.policyTransmuteService.getChangeRequestTypeText(
      model.policyChangeType
    );
    const messageDetails: MessageDetail[] = new Array<MessageDetail>();
    messageDetails.push(new MessageDetail('FirstName', model.requestChange?.customerFirstName));
    messageDetails.push(new MessageDetail('EmailAddress', model.requestChange?.emailAddress));

    if (model.requestChange?.hasAfi && !model.requestChange.hasPartner)
      messageDetails.push(
        new MessageDetail('AgentID', agentIds ? agentIds : String(model.requestChange?.agentId))
      );

    switch (model.policyChangeType) {
      case PolicyChangeRequestType.NAME_CHANGE:
        partnerId = null;
        messageDetails.push(
          new MessageDetail('ChangeField1', model.nameChange?.existingName ?? '')
        );
        messageDetails.push(new MessageDetail('ChangeField2', model.nameChange?.newName ?? ''));
        messageDetails.push(new MessageDetail('ChangeDetails', model.nameChange?.notes ?? ''));
        break;
      case PolicyChangeRequestType.ADDRESS_CHANGE:
        partnerId = null;
        messageDetails.push(
          new MessageDetail('ChangeField1', model.addressChange?.existingAddress ?? '')
        );
        messageDetails.push(
          new MessageDetail('ChangeField2', model.addressChange?.newAddress ?? '')
        );
        messageDetails.push(new MessageDetail('ChangeDetails', model.addressChange?.reason ?? ''));
        break;

      case PolicyChangeRequestType.DRIVER_LICENSE_CHANGE:
        partnerId = null;
        messageDetails.push(new MessageDetail('ChangeField1', ''));
        messageDetails.push(
          new MessageDetail('ChangeField2', model.driverLicenseChange?.licenseNumber ?? '')
        );
        messageDetails.push(
          new MessageDetail('ChangeDetails', model.driverLicenseChange?.reason ?? '')
        );
        messageDetails.push(
          new MessageDetail(
            'policyNumber(s)',
            model.requestChange?.afiPolicyNumbers?.join(',') ?? ''
          )
        );
        break;

      case PolicyChangeRequestType.VIN_CHANGE:
        if (model.vinChange?.policyPartnerId) partnerId = model.vinChange.policyPartnerId;
        messageDetails.push(new MessageDetail('ChangeField1', model.vinChange?.currentVin ?? ''));
        messageDetails.push(new MessageDetail('ChangeField2', model.vinChange?.newVin ?? ''));
        messageDetails.push(new MessageDetail('ChangeDetails', model.vinChange?.reason ?? ''));
        break;

      case PolicyChangeRequestType.MORTGAGE_LENDER_DETAILS_CHANGE: {
        templateCode = 'MYAC_TPI';
        if (model.mortgageChange?.policyPartnerId) partnerId = model.mortgageChange.policyPartnerId;
        let coverageChanges = '';
        if (model.mortgageChange?.newLenderName || model.mortgageChange?.addLenderName)
          coverageChanges = `New mortgage lender name: ${
            model.mortgageChange.newLenderName || model.mortgageChange.addLenderName
          }`;
        if (model.mortgageChange?.lenderToBeRemoved)
          coverageChanges = `Mortgage lender removed from policy: ${model.mortgageChange.lenderToBeRemoved}`;

        messageDetails.push(
          new MessageDetail(
            'PolicyNumber',
            model.mortgageChange?.selectedPolicyNumber
              ? this.utilService.maskPolicyNumber(model.mortgageChange.selectedPolicyNumber)
              : ''
          )
        );
        messageDetails.push(new MessageDetail('ChangeType', requestedChange ?? ''));
        messageDetails.push(
          new MessageDetail('ChangeDetails', model.mortgageChange?.comment ?? '')
        );
        messageDetails.push(new MessageDetail('CoverageChanges', coverageChanges));
        if (model.mortgageChange?.isAfi)
          messageDetails.push(new MessageDetail('AgentID', String(model.requestChange?.agentId)));
        break;
      }
      case PolicyChangeRequestType.POLICY_COVERAGE_CHANGE: {
        const policies = model.coverageChange?.policyList ?? [];
        const afiPolicies = policies.filter(p => p.policyPartnerId === 'AFI');
        const partnerPolicies = policies.filter(p => p.policyPartnerId !== 'AFI');
        if (afiPolicies?.length > 0 && partnerPolicies?.length > 0) partnerId = null;
        else if (afiPolicies?.length > 0) partnerId = 'AFI';
        else if (partnerPolicies?.length > 0) {
          if (partnerPolicies.length === 1) partnerId = partnerPolicies[0]?.policyPartnerId ?? '';
          else partnerId = null;
        }
        const policiesWithRiskDescription = policies
          .map(risk => ` ${new ArrayToList().transform(risk.riskDescriptionList ?? [])}`)
          .join(',');

        messageDetails.push(new MessageDetail('ChangeField2', policiesWithRiskDescription));
        messageDetails.push(
          new MessageDetail('ChangeDetails', model.coverageChange?.comment ?? '')
        );
        break;
      }
    }

    const email = new TemplatedEmail(
      templateCode,
      partnerId ?? '',
      'MyAccount',
      'EN',
      model.requestChange?.customerId ?? '',
      model.requestChange?.emailAddress ?? '',
      messageDetails
    );
    return this.communicationService.sendTemplatedEmail(email);
  }

  /**
   * @description:This function builds and sends the email notification to the agents.
   * @param changeRequest Object
   * @returns Observable of type any
   */
  public sendAgentEmailNotifications(changeModel: ChangeRequest): Observable<unknown> {
    const model = new ChangerequestInternalEmail(changeModel);
    const policyList = model.selectedPolicyNumber
      ? model.policyList.filter((p: Policy) => p.policyNumber === model.selectedPolicyNumber)
      : model.policyList;
    const policyGroup = this.getPolicyGroup(policyList);

    return from(Object.keys(policyGroup)).pipe(
      mergeMap(key => this.store.select(selectSelectedAgentFromStoreFactory(key))),
      mergeMap(producer => {
        const policies = (policyGroup[producer?.id ?? ''] || []) as Policy[];
        if (policies.length > 0 && producer) {
          return this.buildAgentEmailContent(model, producer, policies).pipe(
            mergeMap(emailContent => this.communicationService.sendInternalEmail(emailContent))
          );
        }
        return of(null);
      }),
      catchError(error => throwError(error))
    );
  }

  /**
   * @author Abhishek Singh
   * @param
   * @returns
   * @description
   */
  private buildAgentEmailContent(
    model: ChangerequestInternalEmail,
    producer: Agent,
    policies: Policy[]
  ): Observable<InternalEmail> {
    return this.buildAgentEmailSubject(model, producer, policies).pipe(
      withLatestFrom(this.store.select(selectFirstName)),
      withLatestFrom(this.store.select(selectLastName)),
      mergeMap(([[emailSubjectContent, _firstName], _lastName]) => {
        const agentEmail = producer?.agencyEmails?.[0] ?? producer?.emails?.[0];

        const internalAgentEmail = producer?.agencyEmails?.[0]
          ? env.policyGracePeriodNotificatonInternalAgencyMailbox
          : env.policyGracePeriodNotificatonInternal;

        const emailAddress: string = env.production ? (agentEmail ?? '') : internalAgentEmail;

        // If we do not have the agent email throw an error
        if (!emailAddress) {
          throwError({ message: 'Agent Email not found' });
        }
        let email: InternalEmail = new InternalEmail();
        email = {
          ...email,
          messageFrom: new Addressee(env.policyChangeRequestInternal, 'Policy Change Request'),
          messageToList: [
            new Addressee(emailAddress, `${producer.firstName} ${producer.lastName}`)
          ],
          messageSubject: `Your Customer Has Requested a Change in My Account`,
          messageContent: emailSubjectContent
        };
        return of(email);
      }),
      catchError(error => throwError(error))
    );
  }

  /**
   * @author Abhishek Singh
   * @param
   * @returns
   * @description
   */
  private buildAgentEmailSubject(
    model: ChangerequestInternalEmail,
    producer: Agent,
    policies: Policy[]
  ): Observable<string> {
    return this.store.pipe(
      select(selectPartyDetails),
      withLatestFrom(this.store.select(selectAddress)),
      take(1),
      mergeMap(([partyDetails, primaryAddress]) => {
        let agentContent: string | undefined;
        switch (model.policyChangeType) {
          case PolicyChangeRequestType.NAME_CHANGE:
            agentContent = this.copyService.getCopy(
              `policy.${producer ? 'policyNameChangeAgentEmail' : 'policyNameChangeAdvisorEmail'}`,
              {
                agentName: `${producer.firstName} ${producer.lastName}`,
                existingName: `${partyDetails?.firstName ?? ''} ${partyDetails?.lastName ?? ''}`,
                newName: `${new TitleCasePipe().transform(
                  model.firstName
                )} ${new TitleCasePipe().transform(model.lastName)}`,
                mailingAddress: `${new AddressPipe().transform(primaryAddress, 'long-singleline')}`,
                emailAddress: partyDetails?.primaryEmail?.emailAddress ?? '',
                phoneNumber: new PhoneNumberSingleStringFormatterPipe().transform(
                  partyDetails?.primaryPhone?.tenDigitPhoneNumber ?? ''
                ),
                notes: model.comment,
                policyNumbers: ''
              }
            );
            break;

          case PolicyChangeRequestType.ADDRESS_CHANGE:
            agentContent = this.copyService.getCopy('policy.policyAddressChangeAgentEmail', {
              agentName: `${producer.firstName} ${producer.lastName}`,
              existingName: `${partyDetails?.firstName ?? ''} ${partyDetails?.lastName ?? ''}`,
              existingAddress: `${new AddressPipe().transform(primaryAddress, 'long-singleline')}`,
              newMailingAddress: `${new AddressPipe().transform(model.address, 'long-singleline')}`,
              emailAddress: partyDetails?.primaryEmail?.emailAddress ?? '',
              phoneNumber: new PhoneNumberSingleStringFormatterPipe().transform(
                partyDetails?.primaryPhone?.tenDigitPhoneNumber ?? ''
              ),
              notes: model.comment ?? ''
            });
            break;

          case PolicyChangeRequestType.POLICY_COVERAGE_CHANGE:
            agentContent = this.copyService.getCopy('policy.policyCoverageChangeAgentEmail', {
              agentName: `${producer.firstName} ${producer.lastName}`,
              existingName: `${partyDetails?.firstName ?? ''} ${partyDetails?.lastName ?? ''}`,
              mailingAddress: `${new AddressPipe().transform(primaryAddress, 'long-singleline')}`,
              emailAddress: partyDetails?.primaryEmail?.emailAddress ?? '',
              phoneNumber: new PhoneNumberSingleStringFormatterPipe().transform(
                partyDetails?.primaryPhone?.tenDigitPhoneNumber ?? ''
              ),
              policytext: (policies?.length ?? 0) > 1 ? 'Policies' : 'Policy',
              policiesWithRiskDescription: new ArrayToList().transform(
                model.policyList.flatMap((policy: Policy) => {
                  if (policy.assignedProducer?.producerIdNum === producer.id) {
                    const riskDescription = `<br> Risk Description: ${new ArrayToList().transform(
                      policy.riskDescriptionList ?? []
                    )} <br>
                    Policy Number: ${new PrettyPolicyNum().transform(policy)}`;
                    return riskDescription;
                  }
                  return '';
                }),
                '<br>'
              ),
              notes: model.comment
            });
            break;

          case PolicyChangeRequestType.MORTGAGE_LENDER_DETAILS_CHANGE: {
            let lenderDetails = `<br><b>Requested change:</b> ${model.requestedChange}`;
            let contactDetails = '';
            if (partyDetails?.primaryPhone?.tenDigitPhoneNumber) {
              contactDetails += `<br><b>Customer phone:</b> ${new PhoneNumberSingleStringFormatterPipe().transform(
                partyDetails.primaryPhone.tenDigitPhoneNumber
              )}`;
            }
            if (partyDetails?.primaryEmail?.emailAddress) {
              contactDetails += `<br><b>Customer email address:</b> ${
                partyDetails.primaryEmail.emailAddress
              }`;
            }
            if (model.currentLenderName)
              lenderDetails += `<br><b>Current lender name:</b> ${model.currentLenderName}`;
            if (model.newLenderName || model.addLenderName)
              lenderDetails += `<br><b>New lender name:</b> ${
                model.newLenderName || model.addLenderName
              }`;
            if (model.lenderToBeRemoved)
              lenderDetails += `<br><b>Lender to be removed:</b> ${model.lenderToBeRemoved}`;
            if (model.comment) lenderDetails += `<br><b>Comment:</b> ${model.comment}`;

            agentContent = this.copyService.getCopy('policy.policyMortgageLenderChangeAgentEmail', {
              agentName: `${producer.firstName}`,
              existingName: `${partyDetails?.firstName ?? ''} ${partyDetails?.lastName ?? ''}`,
              policyNumber: model.selectedPolicyNumber,
              contactDetails,
              lenderDetails
            });
            break;
          }

          default:
            break;
        }

        return of(agentContent ?? '');
      }),
      catchError(() => of(''))
    );
  }

  private getPolicyGroup(policyList: Policy[]): Record<string, Policy[]> {
    return policyList.reduce(
      (acc, p) => {
        const key = p.assignedProducer?.producerIdNum ?? '';
        acc[key] = [...(acc[key] ?? []), p];
        return acc;
      },
      {} as Record<string, Policy[]>
    );
  }

  /**
   * @author Abhishek Singh
   * @param
   * @returns
   * @description
   */
  public generateCustomerEmail(changeRequest: ChangeRequest): Observable<unknown> {
    const model = new ChangerequestInternalEmail(changeRequest);
    const policyList = model.selectedPolicyNumber
      ? model.policyList.filter((p: Policy) => p.policyNumber === model.selectedPolicyNumber)
      : model.policyList;

    let level1PartnerId: string | null = null;
    if (policyList.length === 1) level1PartnerId = policyList[0]?.policyPartnerId ?? null;

    const policyGroup = this.getPolicyGroup(policyList);
    const messageDetails: MessageDetail[] = new Array<MessageDetail>();

    return from(Object.keys(policyGroup)).pipe(
      mergeMap(key => this.store.select(selectSelectedAgentFromStoreFactory(key))),
      withLatestFrom(this.store.select(selectEmailAddress)),
      withLatestFrom(this.store.select(selectCustomerId)),
      withLatestFrom(this.store.select(selectFirstName)),
      withLatestFrom(this.store.select(selectPartyDetails)),
      withLatestFrom(this.store.select(selectAddress)),

      mergeMap(
        ([
          [[[[agent, userEmailAddress], customerId], firstName], partyDetails],
          primaryAddress
        ]) => {
          let partnerIdStore: string | null | undefined;
          switch (model.policyChangeType) {
            case PolicyChangeRequestType.NAME_CHANGE:
              messageDetails.push(
                new MessageDetail(
                  'ChangeField1',
                  `${partyDetails?.firstName ?? ''} ${partyDetails?.lastName ?? ''}`
                )
              );
              messageDetails.push(
                new MessageDetail(
                  'ChangeField2',
                  `${new TitleCasePipe().transform(
                    model.firstName
                  )} ${new TitleCasePipe().transform(model.lastName)}`
                )
              );
              messageDetails.push(new MessageDetail('ChangeDetails', model.comment));
              break;
            case PolicyChangeRequestType.ADDRESS_CHANGE:
              messageDetails.push(
                new MessageDetail(
                  'ChangeField1',
                  `${new AddressPipe().transform(primaryAddress, 'long-singleline')}`
                )
              );
              messageDetails.push(
                new MessageDetail(
                  'ChangeField2',
                  `${new AddressPipe().transform(model.address, 'long-singleline')}`
                )
              );
              messageDetails.push(new MessageDetail('ChangeDetails', model.comment));
              break;
            case PolicyChangeRequestType.POLICY_COVERAGE_CHANGE: {
              if (level1PartnerId) partnerIdStore = level1PartnerId;
              else partnerIdStore = null;
              const policiesWithRiskDescription = model.policyList
                .map(risk => ` ${new ArrayToList().transform(risk.riskDescriptionList ?? [])}`)
                .join(',');

              messageDetails.push(new MessageDetail('ChangeField2', policiesWithRiskDescription));
              messageDetails.push(new MessageDetail('ChangeDetails', model.comment ?? ''));
              break;
            }
            case PolicyChangeRequestType.MORTGAGE_LENDER_DETAILS_CHANGE: {
              let coverageChanges = '';
              if (model.newLenderName || model.addLenderName)
                coverageChanges = `New mortgage lender name: ${
                  model.newLenderName || model.addLenderName
                }`;
              if (model.lenderToBeRemoved)
                coverageChanges = `Mortgage lender removed from policy: ${model.lenderToBeRemoved}`;

              messageDetails.push(
                new MessageDetail(
                  'PolicyNumber',
                  model.selectedPolicyNumber
                    ? this.utilService.maskPolicyNumber(model.selectedPolicyNumber)
                    : ''
                )
              );
              messageDetails.push(new MessageDetail('ChangeType', model.requestedChange ?? ''));
              messageDetails.push(new MessageDetail('ChangeDetails', model.comment ?? ''));
              messageDetails.push(new MessageDetail('CoverageChanges', coverageChanges));
              break;
            }
          }

          messageDetails.push(
            new MessageDetail('FirstName', changeRequest.firstName || firstName || '')
          );
          messageDetails.push(new MessageDetail('EmailAddress', userEmailAddress ?? ''));
          messageDetails.push(new MessageDetail('AgentID', agent?.agentId?.toString() ?? ''));

          const email = new TemplatedEmail(
            model.policyChangeType === PolicyChangeRequestType.MORTGAGE_LENDER_DETAILS_CHANGE
              ? 'MYAC_TPI'
              : 'MRTC',
            partnerIdStore ?? '',
            'MyAccount',
            'EN',
            customerId ?? '',
            userEmailAddress ?? '',
            messageDetails
          );
          return this.communicationService.sendTemplatedEmail(email);
        }
      ),
      tap(() =>
        this.store.dispatch(
          fromRouterActions.Go({
            path: ['/policies/changerequest/confirmation'],
            extras: { queryParams: { agentId: changeRequest.addressChange?.agentId } }
          })
        )
      ),
      catchError(() =>
        of(
          this.store.dispatch(
            fromRouterActions.Go({
              path: ['/policies/changerequest/confirmation'],
              extras: { queryParams: { agentId: changeRequest.addressChange?.agentId } }
            })
          )
        )
      )
    );
  }

  /**
   * @author Abhishek Singh
   * @param
   * @returns
   * @description
   */
  public sendANSNotifications(changeModel: ChangeRequest): Observable<unknown> {
    const model = new ChangerequestInternalEmail(changeModel);
    const policyList = model.selectedPolicyNumber
      ? model.policyList.filter((p: Policy) => p.policyNumber === model.selectedPolicyNumber)
      : model.policyList;
    const policyGroup = this.getPolicyGroup(policyList);

    return from(Object.keys(policyGroup)).pipe(
      mergeMap(key => this.store.select(selectSelectedAgentFromStoreFactory(key))),
      mergeMap(producer =>
        this.buildNotificationData(model, String(producer?.id), changeModel.policyList)
      ),
      mergeMap(ansRequest => this.ansNotification(ansRequest)),
      catchError(error => throwError(error))
    );
  }

  /**
   * @author Abhishek Singh
   * @param
   * @returns
   * @description
   */
  private ansNotification(model: ANSRequest): Observable<unknown> {
    const uri = `${env.userAccountApi}/agentNotification/${model.producerId}`;
    return this.http.post(uri, omit(model, ['producerId']));
  }

  /**
   * @author Abhishek Singh
   * @param producerId: Id of the currently selected agent
   * @returns Observable of ANSRequest
   * @description:
   */
  public buildNotificationData(
    model: ChangerequestInternalEmail,
    producerId: string,
    policies: Policy[]
  ): Observable<ANSRequest> {
    return this.store.select(selectAgentInformationByProducerId(producerId)).pipe(
      mergeMap(agent => this.buildANSContentBody(model, agent ?? ({} as Agent), policies)),
      withLatestFrom(this.store.select(selectDisplayName)),
      take(1),
      mergeMap(([ansNotificationBody, displayName]) => {
        const ansNotificationPayload: ANSRequest = {
          notificationsRequest: {
            notificationText: ansNotificationBody,
            notificationType: 'ANSMYREQCH',
            customerName: displayName,
            referenceNumber: model.selectedPolicyNumber || 'NA',
            formattedReferenceNumber: model.selectedPolicyNumber || 'NA',
            policyNumber: '',
            from: 'DMSI',
            dueDate: DateTime.now().plus({ days: 90 }).toJSDate().toISOString(),
            createdBy: 'DMSI',
            uniqueId: this.utilService.generateId(),
            sourceSystem: 'MYACCOUNT'
          },
          producerId: producerId
        };
        return of(ansNotificationPayload);
      }),
      catchError(error => throwError(error))
    );
  }

  /**
   * @author Abhishek Singh
   * @param
   * @returns
   * @description
   */
  private buildANSContentBody(
    model: ChangerequestInternalEmail,
    agent: Agent,
    policies: Policy[]
  ): Observable<string> {
    return this.store.pipe(
      select(selectPartyDetails),
      withLatestFrom(this.store.select(selectAddress)),
      take(1),
      mergeMap(([partyDetails, primaryAddress]) => {
        const primaryEmail = partyDetails?.emails?.find(email => email.primaryIndicator);
        const primaryPhone = partyDetails?.phones?.find(phone => phone.primaryIndicator);
        let agentContent: string | undefined;
        switch (model.policyChangeType) {
          case PolicyChangeRequestType.NAME_CHANGE:
            agentContent = this.copyService.getCopy('policy.policyNameChangeANSMessage', {
              agentName: `${agent.fullName}`,
              existingName: `${partyDetails?.firstName ?? ''} ${partyDetails?.lastName ?? ''}`,
              newName: `${model.firstName} ${model.lastName}`,
              mailingAddress: `${new AddressPipe().transform(primaryAddress, 'long-singleline')}`,
              emailAddress: primaryEmail?.emailAddress,
              phoneNumber: primaryPhone
                ? new PrettyPhoneNumberPipe().transform(primaryPhone.tenDigitPhoneNumber)
                : ' ',
              notes: model.comment,
              policyNumbers: ''
            });
            break;

          case PolicyChangeRequestType.ADDRESS_CHANGE:
            agentContent = this.copyService.getCopy('policy.policyAddressChangeANSMessage', {
              agentName: `${agent.fullName}`,
              existingName: `${partyDetails?.firstName ?? ''} ${partyDetails?.lastName ?? ''}`,
              existingMailingAddress: `${new AddressPipe().transform(
                primaryAddress,
                'long-singleline'
              )}`,
              newMailingAddress: `${new AddressPipe().transform(model.address, 'long-singleline')}`,
              emailAddress: primaryEmail?.emailAddress,
              phoneNumber: primaryPhone
                ? new PrettyPhoneNumberPipe().transform(primaryPhone.tenDigitPhoneNumber)
                : ' ',
              notes: model.comment
            });
            break;

          case PolicyChangeRequestType.POLICY_COVERAGE_CHANGE:
            agentContent = this.copyService.getCopy('policy.policyCoverageChangeANSMessage', {
              agentName: `${agent.fullName}`,
              existingName: `${partyDetails?.firstName ?? ''} ${partyDetails?.lastName ?? ''}`,
              mailingAddress: `${new AddressPipe().transform(primaryAddress, 'long-singleline')}`,
              emailAddress: primaryEmail?.emailAddress,
              phoneNumber: primaryPhone
                ? new PrettyPhoneNumberPipe().transform(primaryPhone.tenDigitPhoneNumber)
                : ' ',
              policytext: (policies?.length ?? 0) > 1 ? 'Policies' : 'Policy',
              policiesWithRiskDescription: new ArrayToList().transform(
                model.policyList.flatMap(
                  (policy: Policy) =>
                    `${policy.policyNumber} - ${new ArrayToList().transform(
                      policy.riskDescriptionList ?? []
                    )}`
                )
              ),
              notes: model.comment
            });
            break;

          case PolicyChangeRequestType.MORTGAGE_LENDER_DETAILS_CHANGE: {
            let lenderDetails = `\nRequested change: ${model.requestedChange}`;
            let contactDetails = '';
            if (primaryPhone?.tenDigitPhoneNumber) {
              contactDetails += `\nCustomer phone: ${new PrettyPhoneNumberPipe().transform(
                primaryPhone.tenDigitPhoneNumber
              )}`;
            }
            if (primaryEmail?.emailAddress) {
              contactDetails += `\nCustomer email address: ${primaryEmail.emailAddress}`;
            }
            if (model.currentLenderName)
              lenderDetails += `\nCurrent lender name: ${model.currentLenderName}`;
            if (model.newLenderName || model.addLenderName)
              lenderDetails += `\nNew lender name: ${model.newLenderName || model.addLenderName}`;
            if (model.lenderToBeRemoved)
              lenderDetails += `\nLender to be removed: ${model.lenderToBeRemoved}`;
            if (model.comment) lenderDetails += `\n\nComment: ${model.comment}`;

            agentContent = this.copyService.getCopy('policy.policyMortgageLenderChangeANSMessage', {
              agentName: `${agent.firstName}`,
              existingName: `${partyDetails?.firstName ?? ''} ${partyDetails?.lastName ?? ''}`,
              policyNumber: model.selectedPolicyNumber,
              contactDetails,
              lenderDetails
            });
            break;
          }

          default:
            break;
        }

        return of(agentContent ?? '');
      }),
      catchError(_error => of(''))
    );
  }

  /**
   * @author Abhishek Singh
   * @param
   * @returns
   * @description
   */
  public sendAnalytic(analyticPayload: PolicyAnalyticActionPayload): void {
    let pageAnalytic = analyticPayload.pageAnalytic;
    if (pageAnalytic) {
      const changeType = analyticPayload.changeTypeSelected;
      const pageName: string =
        changeType !== undefined && !isNaN(changeType)
          ? interpolate(analyticPayload.pageAnalytic.pageName, {
              changeTypeSelected:
                ':' +
                this.policyTransmuteService
                  .getChangeRequestTypeText(changeType)
                  ?.replace(/[ /]/g, '')
            })
          : interpolate(analyticPayload.pageAnalytic.pageName, {
              changeTypeSelected: ''
            });

      pageAnalytic = { ...analyticPayload.pageAnalytic, pageName: pageName };

      if (analyticPayload.policyPageName) {
        pageAnalytic = {
          ...analyticPayload.pageAnalytic,
          pageName:
            interpolate(analyticPayload.pageAnalytic.pageName, {
              changeTypeSelected: ''
            }) +
            ':' +
            analyticPayload.policyPageName
        };
      }
    }

    if (analyticPayload.eventAnalytic) {
      return this.analyticsFacade.trackPage(pageAnalytic);
    }
    return this.analyticsFacade.trackPage(pageAnalytic);
  }

  hasOnlyPartners(policies: Policy[]) {
    return policies.every(p => p.policyPartnerId !== 'AFI');
  }

  hasOnlyPhoenixAuto(policies: Policy[]) {
    return policies.every(
      p =>
        p.sourcePath === SourceSystem.PHOENIX &&
        p.policyType.toUpperCase() === TypeOfPolicy.Auto.toUpperCase()
    );
  }

  hasOnlyPhoenixOrHorisonHome(policies: Policy[]) {
    return policies.every(
      p =>
        [SourceSystem.PHOENIX, SourceSystem.HORISON].includes(p.sourcePath as SourceSystem) &&
        p.policyType.toUpperCase() === TypeOfPolicy.Home.toUpperCase()
    );
  }
}
